import { useEffect, useRef, useCallback, useMemo } from "react";
import { getSocket } from "../api/socket";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

export function useSocket() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const handlersRef = useRef<Map<string, Set<Function>>>(new Map());

  useEffect(() => {
    return () => {
      const socket = getSocket();
      handlersRef.current.forEach((handlers, event) => {
        handlers.forEach((handler) => socket.off(event, handler as any));
      });
      handlersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const socket = getSocket();

    const onFirstBlood = (data: any) => {
      addToast("info", `First Blood: ${data.username} solved "${data.challengeTitle}"`);
    };

    const onNotification = (data: any) => {
      addToast("info", `${data.title}: ${data.message}`);
    };

    socket.on("first-blood", onFirstBlood);
    socket.on("notification", onNotification);

    return () => {
      socket.off("first-blood", onFirstBlood);
      socket.off("notification", onNotification);
    };
  }, [user, addToast]);

  const on = useCallback((event: string, handler: Function) => {
    const socket = getSocket();
    socket.on(event, handler as any);
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler);
  }, []);

  const off = useCallback((event: string, handler: Function) => {
    const socket = getSocket();
    socket.off(event, handler as any);
    handlersRef.current.get(event)?.delete(handler);
  }, []);

  const socket = useMemo(() => user ? getSocket() : null, [user]);

  return { on, off, socket };
}
